import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppIcon } from "@/components/AppIcon";
import { ArrowLeft, ExternalLink, AlertCircle } from "lucide-react";

export default function AppView() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get("/apps")
      .then(({ data }) => {
        const found = data.find((a) => a.app_id === appId);
        if (found) setApp(found);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [appId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-5 w-5 rounded-full border-2 border-brand-blue border-t-transparent animate-spin" />
      </div>
    );
  }

  if (notFound || !app) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4 px-4 text-center">
        <AlertCircle className="h-10 w-10 text-red-500" />
        <h2 className="font-heading text-xl text-slate-900">App non disponibile</h2>
        <p className="text-slate-500">Non hai accesso a questa applicazione o non esiste.</p>
        <button
          data-testid="appview-back-btn"
          onClick={() => navigate("/dashboard")}
          className="mt-2 px-4 py-2 rounded-md brand-gradient text-white text-sm font-medium"
        >
          Torna alla Dashboard
        </button>
      </div>
    );
  }

  // SSO base: passa l'identità dell'HUB alle app integrate
  const separator = app.url.includes("?") ? "&" : "?";
  const src = `${app.url}${separator}hub_user=${encodeURIComponent(user?.email || "")}`;

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      <div className="shrink-0 h-14 backdrop-blur-xl bg-white/80 border-b border-slate-200 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <button
            data-testid="appview-back-btn"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all"
          >
            <ArrowLeft className="h-4 w-4" /> Suite
          </button>
          <div className="h-5 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <span style={{ color: app.color }}>
              <AppIcon name={app.icon} className="h-4 w-4" />
            </span>
            <span className="font-heading text-sm font-medium text-slate-900">{app.name}</span>
          </div>
        </div>
        <a
          data-testid="appview-open-tab"
          href={app.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all"
        >
          Apri in nuova scheda <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      <iframe
        data-testid="appview-iframe"
        title={app.name}
        src={src}
        className="flex-1 w-full border-0 bg-white"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads"
      />
    </div>
  );
}
